import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import "./Home.css";
import { useQuery } from "@apollo/client";
import { useAuth } from "../../auth/AuthProvider";
import { GET_ACCOUNT_QUERY, AccountData } from "../../api/Requests";
import { RxHamburgerMenu } from "react-icons/rx";
import { TfiClose } from "react-icons/tfi";
import { Session } from "@supabase/supabase-js";
import { VscAccount } from "react-icons/vsc";
import { MyBigLoader } from "../../util/myLoader";
import { Footer } from "../../components/Footer/Footer";
import { ToastContainer } from "react-toastify";

// Default easyzilla.com color palette
// Primary: #0538af
// Secondary: #f5f5f5
// Secondary Helper: #FFFFFF
// Accent: #1e4ce9

export default function Home() {
  const { session } = useAuth();
  const [openMenu, setOpenMenu] = useState(false);
  const [openAccountMenu, setOpenAccountMenu] = useState(false);

  const location = useLocation();
  const { pathname } = location;
  const isWebpagePreview = pathname.includes("/webpage");

  function toggleOpenMenu() {
    setOpenMenu(val => !val);
  }

  function toggleOpenAccountMenu(forceOpen?: boolean) {
    if (forceOpen !== undefined) {
      setOpenAccountMenu(forceOpen);
    } else {
      setOpenAccountMenu(val => !val);
    }
  }

  // Pass session full name to help populate the account if needed
  const fullName = session?.user.user_metadata?.full_name
    ? window.btoa(session?.user.user_metadata?.full_name)
    : undefined;
  const { data, error } = useQuery<AccountData>(GET_ACCOUNT_QUERY, {
    skip: session === null,
    variables: {
      metaFullName: fullName,
    },
  });
  // Early return if session isn't available for some reason
  if (!session) {
    return;
  }
  return (
    <div className="home">
      <div className={!isWebpagePreview ? "homeContainer" : ""}>
        <MainMenu
          openMenu={openMenu}
          toggleOpenMenu={toggleOpenMenu}
          openAccountMenu={openAccountMenu}
          toggleOpenAccountMenu={toggleOpenAccountMenu}
          session={session}
          accountData={data}
        />
        {error && <p>Error: {error.message}</p>}
        {!data && <MyBigLoader />}
        {data && (
          <div className="homeOutlet">
            <Outlet context={data} />
          </div>
        )}
      </div>
      <ToastContainer />
      {!isWebpagePreview && <Footer />}
    </div>
  );
}

interface MainMenuProps {
  openMenu: boolean;
  toggleOpenMenu: () => void;
  openAccountMenu: boolean;
  toggleOpenAccountMenu: (forceOpen?: boolean) => void;
  session: Session | null;
  accountData?: AccountData;
}

function MainMenu({
  openMenu,
  toggleOpenMenu,
  openAccountMenu,
  toggleOpenAccountMenu,
  session,
  accountData,
}: MainMenuProps) {
  return (
    <div className="mainMenu">
      <div className="mainMenuContent">
        <div className="mainMenuTitle">
          <Link className="mainMenuItem" to="/">
            <h2>easyzilla</h2>
          </Link>
        </div>
        <div className="mainMenuItems">
          <Link className="mainMenuItem" to="/webpage#top">
            Webpage
          </Link>
          {session?.user.confirmed_at && (
            <Link className="mainMenuItem" to="/settings">
              Settings
            </Link>
          )}
        </div>
        <div className="mobileMainMenu">
          {openMenu && (
            <div className="mobileMainMenuItems">
              <Link className="mobileMainMenuItem" to="/webpage#top">
                Webpage
              </Link>
              {session?.user.confirmed_at && (
                <Link className="mobileMainMenuItem" to="/settings">
                  Settings
                </Link>
              )}
              {openAccountMenu && (
                <div className="accountMenu">
                  <div className="accountMenuItems">
                    <Link className="accountMenuItem" to="/account">
                      Account
                    </Link>
                    <Link className="accountMenuItem" to="/plans">
                      Pricing
                    </Link>
                    <Link
                      className="accountMenuItem accountMenuItemLogout"
                      to="/logout"
                    >
                      Logout
                    </Link>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="accountMenuButton mainMenuItem"
                onClick={() => toggleOpenAccountMenu()}
              >
                <VscAccount
                  size={25}
                  color={openAccountMenu ? "#1e4ce9" : "#0538af"}
                />
              </button>
            </div>
          )}
          <button
            type="button"
            className="mobileMainMenuButton"
            onClick={() => toggleOpenMenu()}
          >
            {openMenu ? <TfiClose /> : <RxHamburgerMenu />}
          </button>
        </div>
      </div>
      <div className="mainMenuDashboard">
        <p>Your Webpage Status:</p>
        <Link className="mainMenuDashboardStatus" to="/settings">
          {accountData?.account.webpageLastPublished ? "Live" : "Unpublished"}
        </Link>
        <div onMouseLeave={() => toggleOpenAccountMenu(false)}>
          {openAccountMenu && (
            <div className="accountMenu">
              <div className="accountMenuItems">
                <Link className="mainMenuItem" to="/info">
                  Webpage Info
                </Link>
                <Link className="accountMenuItem" to="/account">
                  Account
                </Link>
                <Link className="accountMenuItem" to="/plans">
                  Pricing
                </Link>
                <Link
                  className="accountMenuItem accountMenuItemLogout"
                  to="/logout"
                >
                  Logout
                </Link>
              </div>
            </div>
          )}
          <button
            type="button"
            className={
              "accountMenuButton mainMenuItem" +
              (openAccountMenu ? " mainMenuItemOpen" : "")
            }
            onMouseEnter={() => toggleOpenAccountMenu(true)}
          >
            <span className="accountMenuButtonText">
              <VscAccount size={25} style={{ paddingRight: "5px" }} />
              {accountData?.account.fullName || "Options"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
